/**
 * Fetch the per-tenant protected-ID key and import it into a non-extractable,
 * in-memory-only WebCrypto `CryptoKey`.
 *
 * The raw HMAC key crosses to the browser exactly once, over the authenticated
 * `GET /api/uploads/tenant-key` endpoint (the one place it's allowed to — the
 * browser must hold the secret to compute protected IDs the backend can't
 * reverse). This module:
 *
 *   - calls the contract through `apiClient` (so the response is schema-validated);
 *   - immediately imports the base64url key material into a non-extractable
 *     `CryptoKey` via `importHmacKey` and DISCARDS the raw string — it is never
 *     retained, never returned, and never written to storage/IndexedDB/
 *     localStorage. Only the imported `CryptoKey` lives on, held by the caller
 *     (the upload flow) for the duration of the upload and then GC'd.
 *
 * Browser-only: depends on WebCrypto (via `importHmacKey`) and `apiClient`.
 */

import { tenantKeyContract } from '../../common/contracts/uploads';
import { apiClient } from '../api-client';
import { importHmacKey } from './protected-id-crypto';

/**
 * An imported tenant key, ready for `computeProtectedId`. Holds only the
 * non-extractable `CryptoKey` and the opaque key id (for future rotation /
 * correlation) — never the raw key material.
 */
export interface TenantKeyHandle {
  /** Identifies the key version. Opaque to the client; surfaced for rotation. */
  readonly keyId: string;
  /** The imported HMAC-SHA256 key. Non-extractable: only `sign` is permitted. */
  readonly cryptoKey: CryptoKey;
}

/**
 * Fetch the per-tenant key and import it. The raw base64url key material is
 * consumed by `importHmacKey` and not retained past this call.
 *
 * @throws {AppError | ValidationError | ExternalServiceError} from `apiClient` on
 *   HTTP / validation / network failure; or from the codec if the key material is
 *   malformed.
 */
export async function fetchTenantKey(): Promise<TenantKeyHandle> {
  const { tenantKey } = await apiClient(tenantKeyContract);
  const cryptoKey = await importHmacKey(tenantKey.keyMaterial);
  return { keyId: tenantKey.keyId, cryptoKey };
}
